import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { AdminOrderConfirmationActions } from "@/components/admin/AdminOrderConfirmationActions";
import { AdminOrderNoteForm } from "@/components/admin/AdminOrderNoteForm";
import { describeAdminError } from "@/components/admin/adminErrors";
import { formatDateTimeFR, jsonField } from "@/components/admin/adminFormat";
import { getAdminOrderDetail } from "@/lib/admin-api-client";
import { ApiError, type MoneyAmount } from "@/lib/api-client";

/**
 * `/admin/commandes/:id` — équivalent de apps/web/app/(admin)/admin/commandes/[id]/page.tsx :
 * infos générales, adresse de livraison (snapshot immuable, §8.2/RG-34),
 * articles, totaux, timeline `OrderEvent`, note interne, et — uniquement si
 * `status === "PENDING_CONFIRMATION"` — les actions de la file de
 * confirmation téléphonique (VVS_MODELE_INTERFACE.md §15).
 */
export function AdminCommandeDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: order, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "order", id],
    queryFn: () => getAdminOrderDetail(undefined, id as string),
    enabled: Boolean(id),
    retry: false,
  });

  if (isLoading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  if (isError) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div>
        <Link to="/admin/commandes" className="text-sm font-medium text-navy hover:underline">
          ← Retour aux commandes
        </Link>
        <p className="mt-4 text-sm text-red-700">
          {notFound
            ? "Commande introuvable."
            : describeAdminError(error) || "La fiche commande est momentanément indisponible. Réessayez dans un instant."}
        </p>
      </div>
    );
  }

  if (!order) return null;

  const address = order.shippingAddress;

  return (
    <div>
      <Link to="/admin/commandes" className="text-sm font-medium text-navy hover:underline">
        ← Retour aux commandes
      </Link>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Commande {order.orderNumber}</h1>
          <p className="mt-1 text-sm text-muted">Passée le {formatDateTimeFR(order.placedAt)}</p>
        </div>
        <span className="rounded-sm bg-cream2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-navy">
          {order.status}
        </span>
      </div>

      {order.status === "PENDING_CONFIRMATION" ? (
        <div className="mt-6 rounded-sm border border-gold bg-white p-4">
          <p className="mb-3 text-sm font-medium text-ink">
            File de confirmation téléphonique — {order.confirmationAttempts} tentative
            {order.confirmationAttempts > 1 ? "s" : ""} déjà effectuée{order.confirmationAttempts > 1 ? "s" : ""}.
          </p>
          <AdminOrderConfirmationActions orderId={order.id} />
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-sm border border-line bg-white p-4 lg:col-span-2">
          <h2 className="font-display text-lg font-semibold text-ink">Informations</h2>
          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
            <InfoRow label="Statut paiement" value={order.paymentStatus} />
            <InfoRow label="Statut expédition" value={order.fulfillmentStatus} />
            <InfoRow label="Canal" value={order.channel} />
            <InfoRow label="Code de suivi coupon" value={order.couponCode ?? "—"} />
            <InfoRow label="Confirmée le" value={formatDateTimeFR(order.confirmedAt)} />
            <InfoRow label="Expédiée le" value={formatDateTimeFR(order.shippedAt)} />
            <InfoRow label="Livrée le" value={formatDateTimeFR(order.deliveredAt)} />
            <InfoRow label="Annulée le" value={formatDateTimeFR(order.cancelledAt)} />
            {order.cancellationReason ? <InfoRow label="Motif d'annulation" value={order.cancellationReason} /> : null}
            {order.customerNote ? <InfoRow label="Note client" value={order.customerNote} /> : null}
            {order.internalNote ? <InfoRow label="Note interne (fiche)" value={order.internalNote} /> : null}
          </dl>
        </section>

        <section className="rounded-sm border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">Adresse de livraison</h2>
          {address ? (
            <dl className="mt-3 flex flex-col gap-2 text-sm">
              <InfoRow label="Destinataire" value={jsonField(address.recipientName)} />
              <InfoRow label="Téléphone" value={jsonField(address.phoneE164)} />
              <InfoRow label="Quartier" value={jsonField(address.quartier)} />
              <InfoRow label="Adresse" value={jsonField(address.addressLine)} />
              <InfoRow label="Repère" value={jsonField(address.landmark)} />
            </dl>
          ) : (
            <p className="mt-3 text-sm text-muted">Aucune adresse enregistrée.</p>
          )}
        </section>
      </div>

      <section className="mt-6 rounded-sm border border-line bg-white p-4">
        <h2 className="font-display text-lg font-semibold text-ink">Articles</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line text-xs font-medium uppercase tracking-wide text-muted">
              <tr>
                <th className="py-2 pr-4">Article</th>
                <th className="py-2 pr-4">SKU</th>
                <th className="py-2 pr-4">Qté</th>
                <th className="py-2 pr-4">Prix unitaire</th>
                <th className="py-2 pr-4">Total ligne</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <tr key={item.id} className="border-b border-line last:border-0">
                  <td className="py-2 pr-4">
                    {item.productName}
                    {item.variantName ? <span className="text-muted"> — {item.variantName}</span> : null}
                  </td>
                  <td className="py-2 pr-4">{item.sku}</td>
                  <td className="py-2 pr-4">{item.quantity}</td>
                  <td className="py-2 pr-4">{item.unitPrice.formatted ?? item.unitPrice.amountMinor}</td>
                  <td className="py-2 pr-4">{item.lineTotal.formatted ?? item.lineTotal.amountMinor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="mt-4 flex flex-col items-end gap-1 text-sm">
          <TotalRow label="Sous-total" value={order.totals.subtotal} />
          <TotalRow label="Remise" value={order.totals.discount} />
          <TotalRow label="Livraison" value={order.totals.shipping} />
          <TotalRow label="Taxe" value={order.totals.tax} />
          <TotalRow label="Total" value={order.totals.grandTotal} emphasis />
          <TotalRow label="Payé" value={order.totals.paid} />
          <TotalRow label="Remboursé" value={order.totals.refunded} />
        </dl>
      </section>

      <section className="mt-6 rounded-sm border border-line bg-white p-4">
        <h2 className="font-display text-lg font-semibold text-ink">Historique</h2>
        <ol className="mt-3 flex flex-col gap-3">
          {order.events.length === 0 ? (
            <p className="text-sm text-muted">Aucun événement enregistré.</p>
          ) : (
            order.events.map((event) => (
              <li key={event.id} className="border-l-2 border-line pl-3">
                <p className="text-sm font-medium text-ink">
                  {event.type}
                  {event.fromStatus && event.toStatus ? ` — ${event.fromStatus} → ${event.toStatus}` : null}
                </p>
                <p className="text-xs text-muted">
                  {formatDateTimeFR(event.occurredAt)} · {event.actorType}
                </p>
                {event.payload && Object.keys(event.payload).length > 0 ? (
                  <p className="mt-1 text-sm text-ink">
                    {typeof event.payload.note === "string" ? event.payload.note : JSON.stringify(event.payload)}
                  </p>
                ) : null}
              </li>
            ))
          )}
        </ol>

        <div className="mt-4 border-t border-line pt-4">
          <AdminOrderNoteForm orderId={order.id} />
        </div>
      </section>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function TotalRow({ label, value, emphasis }: { label: string; value: MoneyAmount; emphasis?: boolean }) {
  return (
    <div className={["flex w-full max-w-xs justify-between gap-4", emphasis ? "font-semibold text-ink" : "text-muted"].join(" ")}>
      <dt>{label}</dt>
      <dd>{value.formatted ?? value.amountMinor}</dd>
    </div>
  );
}
